import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { getAuthData } from "../../utils/auth";

const OPCIONES_CUOTAS = [3, 6, 9, 12, 18];

const parentescosMap = {
  1: "Padre/Madre",
  2: "Hijo(a)",
  3: "Esposo(a)",
  4: "Hermano(a)",
  5: "Abuelo(a)",
  6: "Tío(a)",
  7: "Primo(a)",
  8: "Otro(a)",
};

const Previsivos = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  /* =========================
       ESTADOS
  ==========================*/
  const [planes, setPlanes] = useState([]);
  const [planSeleccionado, setPlanSeleccionado] = useState(null);
  const [itemsSeleccionados, setItemsSeleccionados] = useState([]);

  const [modalidad_pago, setModalidad] = useState("contado");
  const [cuotas, setCuotas] = useState(3);

  const [cliente, setCliente] = useState({
    nombre: "",
    cedula: "",
    fecha_nacimiento: "",
    edad: "",
    telefono: "",
    celular: "",
    email: "",
    direccion: "",
    documento_identidad: null,
    nombre_documento: "",
  });

  const [clienteId, setClienteId] = useState(null);
  const [familiares, setFamiliares] = useState([]);
  const [items, setItems] = useState([]);

  const auth = getAuthData();
  const token = auth?.token;
  const vendedorId = auth?.user?.id;
  const supervisorId = auth?.user?.supervisor_id;

  /* =========================
       BUSCAR CLIENTE POR CÉDULA
  ==========================*/
  const buscarCliente = async (cedula) => {
    if (!cedula) return;

    try {

      const res = await axios.get(
        `${apiUrl}/api/v1/clientes/${cedula}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = res.data;
      setClienteId(data.id);

      setCliente({
        ...data,
        fecha_nacimiento: data.fecha_nacimiento
          ? new Date(data.fecha_nacimiento).toISOString().split("T")[0]
          : "",
        documento_identidad: null,
        nombre_documento: "",
      });

      setFamiliares(data.familiares || []);
      toast.success("Cliente encontrado.");
    } catch (err) {
      if (err.response?.status === 404) {
        setClienteId(null);
        toast.info("Cliente nuevo.");
      } else {
        toast.error("Error buscando cliente.");
      }
    }
  };

  const handleClienteChange = (e) => {
    const { name, value } = e.target;
    let updatedFields = { [name]: value };

    if (name === "fecha_nacimiento") {
      const edadCalculada = calcularEdad(value);
      updatedFields.edad = edadCalculada.toString();
    }

    setCliente((prev) => ({
      ...prev,
      ...updatedFields,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setCliente((prev) => ({
        ...prev,
        documento_identidad: file,
        nombre_documento: file.name,
      }));
    }
  };

  /* =========================
       CÁLCULOS
  ==========================*/
  const montoAdicional = useMemo(() => {
    return familiares.reduce(
      (acc, f) => acc + (parseFloat(f.precio) || 0),
      0
    );
  }, [familiares]);

  const precioBase = useMemo(() => {
    if (!planSeleccionado) return 0;
    return parseFloat(planSeleccionado.de_contado) || 0;
  }, [planSeleccionado]);

  const precioTotal = useMemo(() => {
    return precioBase + montoAdicional;
  }, [precioBase, montoAdicional]);

  const montoPorCuota = useMemo(() => {
    if (modalidad_pago !== "financiado") return 0;
    if (!cuotas) return 0;
    return precioTotal / cuotas;
  }, [modalidad_pago, cuotas, precioTotal]);

  useEffect(() => {
    if (!planSeleccionado) {
      setItems([]);
      return;
    }

    const nuevoItem = {
      codigo: planSeleccionado.codigo || "PRE-001",
      descripcion: planSeleccionado.descripcion,
      modalidad_pago,
      cuotas: modalidad_pago === "financiado" ? cuotas : 1,
      monto_cuota: modalidad_pago === "financiado" ? montoPorCuota : 0,
      monto_adicional: montoAdicional,
      precio_total: precioTotal,
    };

    setItems([nuevoItem]);

  }, [
    planSeleccionado,
    modalidad_pago,
    cuotas,
    montoAdicional,
    precioTotal,
    montoPorCuota
  ]);

  /* =========================
       FAMILIARES
  ==========================*/
  const agregarFamiliar = () => {
    setFamiliares([
      ...familiares,
      {
        nombre: "",
        cedula: "",
        fecha_nacimiento: "",
        edad: "",
        parentesco: "",
        precio: 0,
      },
    ]);
  };

  const handleFamiliarChange = async (index, e) => {
    const { name, value } = e.target;
    const nuevos = [...familiares];
    nuevos[index][name] = value;

    if (name === "fecha_nacimiento") {
      const edad = calcularEdad(value);
      nuevos[index].edad = edad;
    }

    if (!planSeleccionado) {
      toast.warning("Debe seleccionar un plan primero.");
      return;
    }

    if (
      nuevos[index].edad &&
      nuevos[index].parentesco &&
      planSeleccionado
    ) {
      try {
        const res = await axios.get(
          `${apiUrl}/api/previsivos/calcular_monto`,
          {
            params: {
              plan: planSeleccionado.id,
              parentesco: nuevos[index].parentesco,
              edad: nuevos[index].edad,
            },
          }
        );
        nuevos[index].precio = parseFloat(res.data.monto);
      } catch { }
    }

    setFamiliares(nuevos);
  };

  const calcularEdad = (fecha) => {
    if (!fecha) return "";
    const hoy = new Date();
    const nacimiento = new Date(fecha);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate()))
      edad--;
    return edad;
  };

  const eliminarFamiliar = (index) => {
    const nuevosFamiliares = familiares.filter((_, i) => i !== index);
    setFamiliares(nuevosFamiliares);
  };

  /* =========================
       PLANES
  ==========================*/
  useEffect(() => {
    const fetchPlanes = async () => {

      const res = await axios.get(
        `${apiUrl}/api/planes?tipo_servicio=previsivo`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPlanes(res.data.filter((p) => p.is_active));
    };
    fetchPlanes();
  }, [apiUrl]);

  /* =========================
       AGREGAR AL RESUMEN
  ==========================*/
  const handleAgregar = () => {
    if (!planSeleccionado) {
      toast.warning("Debe seleccionar un plan.");
      return;
    }

    const nuevoItem = {
      codigo: planSeleccionado.codigo || "PRE-001",
      descripcion: planSeleccionado.descripcion,
      modalidad_pago,
      cuotas: modalidad_pago === "financiado" ? cuotas : 1,
      monto_cuota: modalidad_pago === "financiado" ? montoPorCuota : 0,
      monto_adicional: montoAdicional,
      precio_total: precioTotal,
    };

    if (items.length === 0) {
      setItems([nuevoItem]);
    } else {
      setItems([nuevoItem]); // Siempre solo un plan previsivo
    }
  };

  const handleSeleccionarItem = (index) => {
    if (itemsSeleccionados.includes(index)) {
      setItemsSeleccionados(itemsSeleccionados.filter((i) => i !== index));
    } else {
      setItemsSeleccionados([...itemsSeleccionados, index]);
    }
  };

  const handleEliminarItems = () => {
    const nuevosItems = items.filter((_, index) => !itemsSeleccionados.includes(index));
    setItems(nuevosItems);
    setItemsSeleccionados([]); // Limpiar selección tras borrar
  };

  const totalGeneral = useMemo(() => {
    return items.reduce(
      (acc, item) => acc + (parseFloat(item.precio_total) || 0),
      0
    );
  }, [items]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let idClienteFinal = clienteId;

      /* =========================
         CREAR CLIENTE SI NO EXISTE
      ==========================*/
      if (!idClienteFinal) {
        const formData = new FormData();

        formData.append("vendedor_id", vendedorId);
        formData.append("supervisor_id", supervisorId);
        formData.append("nombre", cliente.nombre);
        formData.append("cedula", cliente.cedula);
        formData.append("fecha_nacimiento", cliente.fecha_nacimiento || "");
        formData.append("edad", cliente.edad || 0);
        formData.append("telefono", cliente.telefono || "");
        formData.append("celular", cliente.celular || "");
        formData.append("email", cliente.email || "");
        formData.append("direccion", cliente.direccion || "");
        formData.append("nombre_documento", cliente.nombre_documento || "");

        if (cliente.documento_identidad instanceof File) {
          formData.append(
            "documento_identidad",
            cliente.documento_identidad,
            cliente.documento_identidad.name
          );
        }

        const response = await axios.post(
          `${apiUrl}/api/clientes`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        idClienteFinal = response.data.cliente_id;
      }

      /* =========================
         CREAR CONTRATO PREVISIVO
      ==========================*/

      const contratoData = {
        cliente_id: clienteId,
        vendedor_id: vendedorId,
        codigo: "PRE000001",
        tipo_servicio: "previsivo",
        tipo_plan: "servicio",
        descripcion: planSeleccionado.descripcion,
        modalidad_pago,
        numero_cuotas: modalidad_pago === "financiado" ? cuotas : null,
        monto_por_cuota: modalidad_pago === "financiado" ? montoPorCuota : null,
        precio_plan: precioTotal,
        fecha_vencimiento: new Date(
          new Date().setDate(new Date().getDate() + 3)
        ).toISOString(),
        detalles_adicionales: {
          codigo: "PRE000001",
          tipo_servicio: "previsivo",
          descripcion: planSeleccionado.descripcion,
          monto_adicional: montoAdicional
        },


        familiares: familiares.map((f) => ({
          nombre: f.nombre,
          cedula: f.cedula,
          fecha_nacimiento: f.fecha_nacimiento,
          edad: parseInt(f.edad),
          parentesco: parentescosMap[f.parentesco] || "",
        })),
      };

      const contratoResponse = await axios.post(
        `${apiUrl}/api/previsivos`,
        [contratoData],
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Contrato creado:", contratoResponse.data);
      toast.success("Solicitud creada correctamente");

      /* =========================
         RESET
      ==========================*/

      setCliente({
        nombre: "",
        cedula: "",
        fecha_nacimiento: "",
        edad: "",
        telefono: "",
        celular: "",
        email: "",
        direccion: "",
        documento_identidad: null,
        nombre_documento: "",
      });

      setFamiliares([]);
      setPlanSeleccionado(null);
      setItems([]);
      setCuotas(3);
      setClienteId(null);

    } catch (error) {
      console.error(error);
      toast.error(
        `Error: ${error.response?.data?.detail || error.message}`
      );
    }
  };

  /* =========================
       RENDER
  ==========================*/
  const formatPrecio = (v) => `$${parseFloat(v || 0).toFixed(2)}`;

  return (
    <Box component="form"
      onSubmit={handleSubmit}
      sx={{ p: 3, boxShadow: 3, background: "#fff", borderRadius: 2 }}>
      <Typography
        sx={{
          marginBottom: "5px",
          fontFamily: "Bakbak One, sans-serif",
          color: "#0A4575",
          textAlign: "center",
        }}
        variant="h5"
      >
        SOLICITUD DE CONTRATO PREVISIVO
      </Typography>

      <Typography
        sx={{
          marginTop: "15px",
          marginBottom: "5px",
          fontFamily: "Bakbak One, sans-serif",
          color: "#0A4575",
          mb: 2,
        }}
        variant="h6"
      >
        DATOS DEL CLIENTE
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <TextField fullWidth label="Nombre Solicitante" size="small" name="nombre" value={cliente.nombre} onChange={handleClienteChange} required sx={{ boxShadow: 3 }} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth label="Cédula o Rif" size="small" name="cedula" value={cliente.cedula} onChange={handleClienteChange} onBlur={(e) => buscarCliente(e.target.value)} required sx={{ boxShadow: 3 }} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth label="Fecha de Nacimiento" type="date" size="small" name="fecha_nacimiento" value={cliente.fecha_nacimiento || ""} onChange={handleClienteChange} required sx={{ boxShadow: 3 }} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField fullWidth label="Edad" size="small" value={cliente.edad} sx={{ boxShadow: 3 }} InputProps={{ readOnly: true }} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth label="Teléfono" size="small" name="telefono" value={cliente.telefono} onChange={handleClienteChange} required sx={{ boxShadow: 3 }} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField fullWidth label="Celular" size="small" name="celular" value={cliente.celular} onChange={handleClienteChange} required sx={{ boxShadow: 3 }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Email" size="small" name="email" value={cliente.email} onChange={handleClienteChange} required sx={{ boxShadow: 3 }} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Dirección" size="small" name="direccion" value={cliente.direccion} onChange={handleClienteChange} required sx={{ boxShadow: 3 }} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <input accept="image/*" id="file-upload" type="file" style={{ height: "1px", width: "1px", position: "absolute" }} onChange={handleFileChange} />
          <label htmlFor="file-upload">
            <Button variant="contained" component="span" sx={{ boxShadow: 3, backgroundColor: "#0A4575", color: "#FFFFFF", "&:hover": { backgroundColor: "#083a5e" } }}>
              Subir Documento de Identidad
            </Button>
          </label>
        </Grid>
        <Grid item xs={12} sm={2}>
          {cliente.nombre_documento && (
            <Typography sx={{ marginTop: "10px", fontFamily: "Abel" }}>
              <b>{cliente.nombre_documento}</b>
            </Typography>
          )}
        </Grid>
      </Grid>

      <Typography sx={{ marginTop: "15px", marginBottom: "10px", fontFamily: "Bakbak One, sans-serif", color: "#0A4575" }} variant="h6">
        PLAN Y MODALIDAD
      </Typography>

      <Grid container spacing={2} alignItems="center"> {/* Agregamos alignItems center para nivelar altura */}
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small" sx={{ boxShadow: 3 }}>
            <InputLabel>Plan</InputLabel>
            <Select
              value={planSeleccionado?.id || ""}
              label="Plan"
              onChange={(e) => setPlanSeleccionado(planes.find(p => p.id === e.target.value))}
            >
              {planes.map(plan => <MenuItem key={plan.id} value={plan.id}>{plan.descripcion}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={2}>
          <FormControl fullWidth size="small" sx={{ boxShadow: 3 }}>
            <InputLabel>Modalidad</InputLabel>
            <Select value={modalidad_pago} onChange={(e) => setModalidad(e.target.value)} label="Modalidad">
              <MenuItem value="contado">Contado</MenuItem>
              <MenuItem value="financiado">Financiado</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {modalidad_pago === "financiado" && (
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" sx={{ boxShadow: 3 }}>
              <InputLabel>Cuotas</InputLabel>
              <Select value={cuotas} onChange={(e) => setCuotas(e.target.value)} label="Cuotas">
                {OPCIONES_CUOTAS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        )}

        <Grid item xs={12} sm={2}>
          <TextField
            fullWidth
            size="small"
            label="Precio Total"
            value={formatPrecio(precioTotal)}
            InputProps={{ readOnly: true }}
            sx={{ boxShadow: 3 }}
          />
        </Grid>

        {/* BOTÓN ALINEADO: Lo ponemos dentro del mismo Grid container */}
        <Grid item xs={12} sm={modalidad_pago === "financiado" ? 3 : 5}>
          <Button
            variant="contained"
            sx={{
              background: "#0A4575",
              height: "40px", // Misma altura aproximada que los TextFields "small"
              boxShadow: 3
            }}
            onClick={handleAgregar}
          >
            Agregar al Resumen
          </Button>
        </Grid>
      </Grid>

      <Typography sx={{ fontFamily: "Bakbak One, sans-serif", color: "#0A4575", mt: 3, mb: 1 }} variant="h6">
        ASOCIAR FAMILIARES
      </Typography>
      <Grid item xs={12} sm={12} sx={{ mb: 2 }}>
        <Button variant="contained" sx={{ background: "#0A4575", mr: 2 }} onClick={agregarFamiliar}>
          Añadir Familiar
        </Button>
        <Button
          sx={{
            backgroundColor: "#0A4575",
          }}
          variant="contained"
          onClick={() => eliminarFamiliar(familiares.length - 1)}  // ← usa familiares.length
          disabled={familiares.length === 0}                       // ← aquí también
        >
          Eliminar Familiar
        </Button>
      </Grid>

      {familiares.map((f, index) => (
        <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Nombre" value={f.nombre} onChange={(e) => handleFamiliarChange(index, e)} name="nombre" sx={{ boxShadow: 3 }} /></Grid>
          <Grid item xs={12} sm={2}><TextField fullWidth size="small" label="Cédula" value={f.cedula} onChange={(e) => handleFamiliarChange(index, e)} name="cedula" sx={{ boxShadow: 3 }} /></Grid>
          <Grid item xs={12} sm={2}><TextField fullWidth size="small" type="date" value={f.fecha_nacimiento} onChange={(e) => handleFamiliarChange(index, e)} name="fecha_nacimiento" sx={{ boxShadow: 3 }} InputLabelProps={{ shrink: true }} /></Grid>
          <Grid item xs={12} sm={2}><TextField fullWidth size="small" label="Edad" value={f.edad} InputProps={{ readOnly: true }} sx={{ boxShadow: 3 }} /></Grid>
          <Grid item xs={12} sm={2}>
            <FormControl fullWidth size="small" sx={{ boxShadow: 3 }}>
              <InputLabel>Parentesco</InputLabel>
              <Select value={f.parentesco} name="parentesco" onChange={(e) => handleFamiliarChange(index, e)} label="Parentesco">
                {Object.entries(parentescosMap).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      ))}

      {items.length > 0 && (
        <>
          <Typography
            sx={{
              marginTop: "25px",
              fontFamily: "Bakbak One, sans-serif",
              color: "#0A4575",
            }}
            variant="h6"
            gutterBottom
          >
            RESUMEN DEL CONTRATO
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead sx={{ background: "#0A4575" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff" }}>Código</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Descripción</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Modalidad</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Cuotas</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Monto Cuota</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Monto Adicional</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Total</TableCell>
                  <TableCell sx={{ color: "#fff" }}>Acción</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, i) => (
                  <TableRow key={i}>
                    <TableCell>{item.codigo}</TableCell>
                    <TableCell>{item.descripcion}</TableCell>
                    <TableCell>{item.modalidad_pago}</TableCell>
                    <TableCell>{item.cuotas}</TableCell>
                    <TableCell>{formatPrecio(item.monto_cuota)}</TableCell>
                    <TableCell>{formatPrecio(item.monto_adicional)}</TableCell>
                    <TableCell>{formatPrecio(item.precio_total)}</TableCell>
                    <TableCell>
                      <Checkbox checked={itemsSeleccionados.includes(i)} onChange={() => handleSeleccionarItem(i)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 3, textAlign: "right" }}>
            <Typography sx={{ fontSize: 20, fontWeight: "bold" }}>
              TOTAL: {formatPrecio(totalGeneral)}
            </Typography>
          </Box>
        </>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 3 }}>
        <Button variant="contained" component={Link} to="/ventas" startIcon={<ArrowBack />} sx={{ backgroundColor: "#0A4575", color: "#FFFFFF", "&:hover": { backgroundColor: "#083a5e" } }}>
          Volver
        </Button>
        <Box>
          <Button
            variant="outlined"
            color="error"
            sx={{
              backgroundColor: "#0A4575",
              color: "#FFFFFF !important",
              borderColor: "#0A4575",
              "&:hover": { backgroundColor: "#083a5e !important" },
              marginRight: 2,
            }}
            onClick={handleEliminarItems}
            disabled={itemsSeleccionados.length === 0}
          >
            Eliminar Ítems
          </Button>
          <Button type="submit" variant="contained" sx={{ backgroundColor: "#0A4575", color: "#FFFFFF", "&:hover": { backgroundColor: "#083a5e" } }}>
            Guardar Solicitud
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Previsivos;